<script lang="ts" setup>
import { computed, inject, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import axiosInstance from '@/plugins/axios'
import { useToast } from 'primevue/usetoast'
import SettingsPageTemplate from '@/components/templates/SettingsPageTemplate.vue'
import DynamicSettingsForm from '@/components/forms/DynamicSettingsForm.vue'
import type { DynamicSettingsField } from '@/components/forms/DynamicSettingsForm.vue'
import { usePageHeader } from '@/composables/usePageHeader'

interface TranslationOption {
  _id: string
  languageIdentifier: string
  languageCode?: string
}

interface TenantDetails {
  _id?: string
  name: string
  active?: boolean
  domains: string[]
  theme: unknown
  settings: {
    active?: boolean
    defaultLanguage: string
  }
  adminSettings: {
    enabledLanguages: string[]
    active: boolean
    reportsConnectionString: string
  }
  updatedAt?: string
}

const createEmptyTenantDetails = (): TenantDetails => ({
  name: '',
  active: false,
  domains: [],
  theme: {},
  settings: {
    active: false,
    defaultLanguage: ''
  },
  adminSettings: {
    enabledLanguages: [],
    active: false,
    reportsConnectionString: ''
  }
})

const normalizeTenantDetails = (data: Record<string, any>): TenantDetails => ({
  ...createEmptyTenantDetails(),
  ...data,
  active: data.active ?? false,
  domains: Array.isArray(data.domains) ? data.domains : [],
  theme: data.theme ?? {},
  settings: {
    active: false,
    defaultLanguage: '',
    ...(data.settings || {})
  },
  adminSettings: {
    enabledLanguages: Array.isArray(data.adminSettings?.enabledLanguages)
      ? data.adminSettings.enabledLanguages
      : [],
    active: data.adminSettings?.active ?? false,
    reportsConnectionString: data.adminSettings?.reportsConnectionString ?? ''
  }
})

const clonePlain = <T,>(value: T): T => JSON.parse(JSON.stringify(value)) as T

const gF = inject('gF') as any
const toast = useToast()
const route = useRoute()
const pageHeader = usePageHeader()

const themeName = ref('')
const isSaving = ref(false)
const tenantDetails = ref<TenantDetails>(createEmptyTenantDetails())
const domainWhitelist = ref<string[]>([])
const allTranslations = ref<TranslationOption[]>([])
const formData = ref<Record<string, unknown>>({})
const initialSnapshot = ref('')

const enabledLanguageOptions = computed(() =>
  allTranslations.value
    .filter((lang) => tenantDetails.value.adminSettings.enabledLanguages.includes(lang._id))
    .map((lang) => ({
      label: lang.languageIdentifier,
      value: lang._id
    }))
)

const tenantFields = computed<DynamicSettingsField[]>(() => [
  {
    key: 'tenantName',
    label: 'Tenant Name',
    type: 'text',
    required: true
  },
  {
    key: 'tenantActive',
    label: 'Tenant Active',
    type: 'toggle'
  },
  {
    key: 'themeName',
    label: 'Theme',
    type: 'text',
    disabled: true,
    help: 'The connected tenant theme.'
  },
  {
    key: 'reportsConnectionString',
    label: 'MongoDB Url',
    type: 'text',
    disabled: true,
    class: 'field-wrapper--full',
    help: 'The connection string can be generated and set via the cli tool.'
  }
])

const buildSnapshot = () =>
  JSON.stringify({
    name: tenantDetails.value.name,
    active: tenantDetails.value.active,
    domains: tenantDetails.value.domains,
    theme: tenantDetails.value.theme,
    settings: tenantDetails.value.settings,
    adminSettings: tenantDetails.value.adminSettings
  })

const isDirty = computed(
  () => initialSnapshot.value !== '' && buildSnapshot() !== initialSnapshot.value
)

const syncFormDataFromTenant = () => {
  formData.value = {
    tenantName: tenantDetails.value.name,
    tenantActive: tenantDetails.value.adminSettings.active,
    themeName: themeName.value || 'No theme connected',
    reportsConnectionString: tenantDetails.value.adminSettings.reportsConnectionString
  }
}

const markCurrentStateAsSaved = () => {
  initialSnapshot.value = buildSnapshot()
}

watch(
  () => tenantDetails.value.name,
  (tenantName) => {
    pageHeader.setTitle(typeof tenantName === 'string' && tenantName.trim() ? tenantName : null)
  },
  { immediate: true }
)

onBeforeUnmount(() => {
  pageHeader.setTitle(null)
})

const onFieldChange = ({ key, value }: { key: string; value: unknown }) => {
  if (key === 'tenantName') {
    tenantDetails.value.name = String(value ?? '')
    return
  }

  if (key === 'tenantActive') {
    tenantDetails.value.adminSettings.active = Boolean(value)
  }
}

const getConnectedTheme = async () => {
  const tenantTheme = tenantDetails.value.theme
  const hasTheme =
    typeof tenantTheme === 'string'
      ? !!tenantTheme
      : !!(tenantTheme && typeof tenantTheme === 'object' && Object.keys(tenantTheme).length)

  if (hasTheme) {
    const res = await axiosInstance.get(`/theme/themeNameById/${tenantDetails.value._id}`)
    themeName.value = res.data.name
  } else {
    themeName.value = 'No theme connected'
  }
}

const getTenantDetails = async () => {
  const tenantId = route.params.id
  const res = await axiosInstance.get(`/admin/getTenant/${tenantId}`)
  tenantDetails.value = normalizeTenantDetails(res.data)
  await getConnectedTheme()
  syncFormDataFromTenant()
  markCurrentStateAsSaved()
}

const resetForm = async () => {
  await getTenantDetails()
}

const getDomainWhitelist = async () => {
  try {
    const res = await axiosInstance.get('/admin/getDomainWhitelist')
    const validDomains = res.data.filter((domain: unknown) => typeof domain === 'string')
    const cleanedDomains = validDomains.map((domain: string) => domain.replace(/^https?:\/\//, ''))

    cleanedDomains.push('localhost')
    domainWhitelist.value = cleanedDomains
  } catch (error) {
    console.error('Error fetching domain whitelist:', error)
  }
}

const getAllTranslations = async () => {
  const res = await axiosInstance.get('/admin/getAllTranslationsMinified')
  allTranslations.value = res.data
}

const getLanguageIdentifier = (langId: string) => {
  const lang = allTranslations.value.find((entry) => entry._id === langId)
  return lang ? lang.languageIdentifier : ''
}

const saveTenant = async () => {
  try {
    isSaving.value = true
    gF.startLoading()
    await axiosInstance.put(
      `/admin/updateTenant/${tenantDetails.value._id}`,
      clonePlain(tenantDetails.value)
    )
    await getTenantDetails()
    toast.add({ severity: 'success', summary: 'Success', detail: 'Tenant saved successfully' })
  } catch (error) {
    console.error('Error saving tenant:', error)
    toast.add({ severity: 'error', summary: 'Error', detail: 'Error saving tenant' })
  } finally {
    gF.endLoading()
    isSaving.value = false
  }
}

onMounted(async () => {
  gF.startLoading()
  try {
    await Promise.all([getAllTranslations(), getDomainWhitelist()])
    await getTenantDetails()
  } finally {
    gF.endLoading()
  }
})
</script>

<template>
  <SettingsPageTemplate
    use-header-actions
    :dirty="isDirty"
    :loading="isSaving"
    :save-disabled="isSaving || !isDirty"
    :cancel-disabled="isSaving"
    :cancel-navigates-back="false"
    :last-updated="tenantDetails.updatedAt"
    @save="saveTenant"
    @cancel="resetForm"
  >
    <template #settings-content>
      <section class="settings-section">
        <div class="settings-section-header">
          <h2 class="settings-section-title">Base Configuration</h2>
          <p class="settings-section-copy">General tenant metadata and the connected theme.</p>
        </div>

        <DynamicSettingsForm
          v-model="formData"
          :fields="tenantFields"
          :disabled="isSaving"
          :columns="2"
          @field-change="onFieldChange"
        />
      </section>

      <section class="settings-section">
        <div class="settings-section-header">
          <h2 class="settings-section-title">Languages and Domains</h2>
          <p class="settings-section-copy">
            Configure the languages available to the tenant and the allowed domains.
          </p>
        </div>

        <div class="settings-stack">
          <div class="settings-row">
            <div class="settings-row-label">
              <label for="enabledLanguages">Enabled Languages</label>
            </div>
            <div class="settings-row-content">
              <MultiSelect
                id="enabledLanguages"
                v-model="tenantDetails.adminSettings.enabledLanguages"
                :options="allTranslations"
                optionLabel="languageIdentifier"
                optionValue="_id"
                filter
                display="chip"
                placeholder="Select Languages"
                class="w-full"
              >
                <template #option="slotProps">
                  <div class="flex items-center">
                    <span
                      v-if="slotProps.option.languageCode === 'en'"
                      class="flag-icon fi fi-gb mr-3"
                    ></span>
                    <span
                      v-else
                      :class="`fi fi-${slotProps.option.languageCode}`"
                      class="flag-icon mr-3"
                    ></span>
                    <div>{{ slotProps.option.languageIdentifier }}</div>
                    <div class="language-code">[{{ slotProps.option.languageCode }}]</div>
                  </div>
                </template>

                <template #value="slotProps">
                  <div class="flex items-center">
                    <div>
                      <Tag
                        v-for="val in slotProps.value"
                        :key="val"
                        severity="secondary"
                        class="mr-2"
                      >
                        {{ getLanguageIdentifier(val) }}
                      </Tag>
                    </div>
                  </div>
                </template>
              </MultiSelect>
            </div>
          </div>

          <div class="settings-row">
            <div class="settings-row-label">
              <label for="defaultLanguage">Default Language</label>
            </div>
            <div class="settings-row-content">
              <Select
                id="defaultLanguage"
                v-model="tenantDetails.settings.defaultLanguage"
                :options="enabledLanguageOptions"
                optionLabel="label"
                optionValue="value"
                :placeholder="
                  enabledLanguageOptions.length
                    ? 'Select default language'
                    : 'Select languages first'
                "
                class="w-full"
              />
              <small class="row-help">
                This language is used as the primary fallback for the tenant.
              </small>
            </div>
          </div>

          <div class="settings-row">
            <div class="settings-row-label">
              <label for="domains">Domains</label>
            </div>
            <div class="settings-row-content">
              <MultiSelect
                id="domains"
                v-model="tenantDetails.domains"
                :options="domainWhitelist"
                filter
                display="chip"
                placeholder="Select connected domains"
                class="w-full"
              >
                <template #option="slotProps">
                  <div class="flex items-center">
                    <div>{{ slotProps.option }}</div>
                  </div>
                </template>

                <template #value="slotProps">
                  <div class="flex items-center">
                    <div>
                      <Tag
                        v-for="val in slotProps.value"
                        :key="val"
                        severity="secondary"
                        class="mr-2"
                      >
                        {{ val }}
                      </Tag>
                    </div>
                  </div>
                </template>
              </MultiSelect>

              <Message size="small" severity="secondary" variant="simple">
                Please note: Only domains listed in the Domain Whitelist file on the Express server
                can be added here.
              </Message>
            </div>
          </div>
        </div>
      </section>
    </template>
  </SettingsPageTemplate>
</template>

<style scoped>
.section-heading {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.form-title {
  font-weight: 600;
  font-size: 1rem;
}

.form-subtitle {
  font-size: 0.85rem;
  color: #6b7280;
}

.settings-section {
  padding: 1.25rem;
  border: 1px solid #ece9e1;
  border-radius: 14px;
  background: #ffffff;
}

.settings-section-header {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  margin-bottom: 1rem;
}

.settings-section-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 650;
  color: #1f2937;
}

.settings-section-copy {
  margin: 0;
  font-size: 0.84rem;
  color: #6b7280;
}

:deep(.field-wrapper--full) {
  grid-column: 1 / -1;
}

.settings-stack {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.settings-row {
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  gap: 1rem;
  align-items: start;
}

.settings-row-label {
  padding-top: 0.6rem;
}

.settings-row-label label {
  font-weight: 600;
  color: #111827;
}

.settings-row-content {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.row-help {
  font-size: 0.8rem;
  color: #6b7280;
}

.language-code {
  margin-left: auto;
  padding-left: 0.75rem;
}

@media (max-width: 900px) {
  .settings-row {
    grid-template-columns: 1fr;
    gap: 0.45rem;
  }

  .settings-row-label {
    padding-top: 0;
  }
}
</style>
